import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SendInviteCodeModal from '../components/SendInviteCodeModal';
import './Admin.css';

function Admin() {
  const [activeTab, setActiveTab] = useState('invite-codes'); // Default to invite-codes tab
  const [inviteCodes, setInviteCodes] = useState([]); // State to store invite codes
  const [loading, setLoading] = useState(true); // State to manage loading status
  const [newInviteCodeData, setNewInviteCodeData] = useState({
    expiration_hours: 48, // Default expiration to 48 hours
    route: '', // Changed to default empty string for dropdown
    description: '',
  }); // State for new invite code form (removed created_by)
  const [editingId, setEditingId] = useState(null); // State to track which row is being edited
  const [editedData, setEditedData] = useState({}); // State to hold data for the currently edited row
  const [showEmailModal, setShowEmailModal] = useState(false); // State to control email modal visibility
  const [selectedInviteCode, setSelectedInviteCode] = useState(null); // State to hold invite code data for the modal

  // Mapping for route database value to display text
  const routeDisplayMap = {
    '/new-client': 'Onboard New Retail Client',
    '/new-nonprofit-llc': 'Onboard new Non-Profit LLC Formation Client',
    // Add other mappings here as needed
  };

  const getRouteDisplayText = (routeValue) => {
    return routeDisplayMap[routeValue] || routeValue; // Return display text or raw value if no mapping
  };

  useEffect(() => {
    const fetchInviteCodes = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/invite-codes/`);
        setInviteCodes(response.data);
      } catch (error) {
        console.error('Error fetching invite codes:', error);
        // Optionally set an error state to display a message to the user
      } finally {
        setLoading(false);
      }
    };

    fetchInviteCodes();
  }, []); // Empty dependency array ensures this runs once on mount

  const handleNewInputChange = (e) => {
    const { name, value } = e.target;
    setNewInviteCodeData({ ...newInviteCodeData, [name]: value });
  };

  const handleCreateInviteCode = async () => {
    try {
      // Basic validation (can be expanded)
      if (!newInviteCodeData.expiration_hours || !newInviteCodeData.route) {
        alert('Expiration (Hours) and Route are required.');
        return;
      }

      // Calculate expiration datetime based on current time + hours
      const now = new Date();
      const expirationDateTime = new Date(now.getTime() + newInviteCodeData.expiration_hours * 60 * 60 * 1000);

      const payload = {
        expiration_datetime: expirationDateTime.toISOString(), // Send ISO string to backend
        route: newInviteCodeData.route, // Route is already the correct value from the dropdown
        description: newInviteCodeData.description,
        // created_by is removed from here as it's not in the form
      };

      const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/invite-codes/`, payload);
      
      // Add the newly created invite code to the state and clear the form
      setInviteCodes([...inviteCodes, response.data]);
      setNewInviteCodeData({
        expiration_hours: 48, // Reset to default hours
        route: '', // Reset route dropdown
        description: '',
      });
    } catch (error) {
      console.error('Error creating invite code:', error);
      alert('Failed to create invite code.');
    }
  };

  const handleEditClick = (code) => {
    setEditingId(code.id);
    setEditedData({
      expiration_datetime: code.expiration_datetime, // Keep as is for date picker
      route: code.route,
      // created_by is removed
      description: code.description || '',
    });
  };

  const handleEditedInputChange = (e) => {
    const { name, value } = e.target;
     setEditedData({ ...editedData, [name]: value });
  };

  const handleSaveClick = async (id) => {
    try {
      // Format date for backend (assuming backend expects ISO 8601)
      const payload = {
         ...editedData,
         // Ensure expiration_datetime is in ISO format if it was changed
         expiration_datetime: typeof editedData.expiration_datetime === 'string'
         ? new Date(editedData.expiration_datetime).toISOString()
         : editedData.expiration_datetime, // Keep as is if it's already a Date object from initial state or not changed
      };

      const response = await axios.put(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/invite-codes/${id}`, payload);

      // Update the state with the data returned from the backend
      setInviteCodes(inviteCodes.map(code => 
        code.id === id ? response.data : code
      ));
      
      setEditingId(null);
      setEditedData({});
    } catch (error) {
      console.error('Error saving invite code:', error);
      alert('Failed to save invite code.');
    }
  };

  const handleCancelClick = () => {
    setEditingId(null);
    setEditedData({});
  };

  const handleEmailClick = (inviteCode) => {
    setSelectedInviteCode(inviteCode);
    setShowEmailModal(true);
  };

  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
    setSelectedInviteCode(null);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'invite-codes':
        return (
          <div>
            <h2>Manage Invite-codes</h2>
            {/* Create New Invite Code Form */}
            <div className="create-invite-code-form">
              <h3>Create New Invite Code</h3>
              <div>
                <label>Expiration (Hours):</label> {/* Changed label */}
                <input
                  type="number" // Changed input type
                  name="expiration_hours" // Changed name to expiration_hours
                  value={newInviteCodeData.expiration_hours} // Use expiration_hours state
                  onChange={handleNewInputChange}
                  min="1" // Ensure at least 1 hour
                />
              </div>
              <div>
                <label>Route:</label> {/* Changed to dropdown */}
                <select
                  name="route"
                  value={newInviteCodeData.route}
                  onChange={handleNewInputChange}
                >
                  <option value="">Select a route</option> {/* Default empty option */}
                  <option value="/new-client">Onboard New Retail Client</option> {/* Added option */}
                  <option value="/new-nonprofit-llc">Onboard new Non-Profit LLC Formation Client</option>
                   {/* Add other route options here as needed */}
                </select>
              </div>
              {/* Removed Created By field */}
              <div>
                <label>Description:</label>
                <input
                  type="text"
                  name="description"
                  value={newInviteCodeData.description}
                  onChange={handleNewInputChange}
                />
              </div>
              <button onClick={handleCreateInviteCode}>Create Invite Code</button>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Invite Code</th>
                  <th>Expiration</th>
                  <th>Route</th>
                  {/* Removed Created By header */}
                  <th>Is Used</th>
                  <th>Used At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6">Loading invite codes...</td> {/* Updated colspan */}
                  </tr>
                ) : ( inviteCodes.map((code) => (
                  <tr key={code.id}>
                    <td>{code.invite_code}</td>
                    <td>
                      {editingId === code.id ? (
                         <input
                          type="datetime-local"
                          name="expiration_datetime"
                          value={editedData.expiration_datetime ? editedData.expiration_datetime.substring(0, 16) : ''} // Format for datetime-local input
                          onChange={handleEditedInputChange}
                        />
                      ) : (
                        new Date(code.expiration_datetime).toLocaleString()
                      )}
                    </td>
                    <td>
                       {/* Display dropdown for editing, but text for viewing */}
                       {editingId === code.id ? (
                         <select
                           name="route"
                           value={editedData.route}
                           onChange={handleEditedInputChange}
                         >
                            <option value="">Select a route</option> {/* Default empty option */}
                            <option value="/new-client">Onboard New Retail Client</option> {/* Added option */}
                            <option value="/new-nonprofit-llc">Onboard new Non-Profit LLC Formation Client</option>
                            {/* Add other route options here as needed for editing */}
                         </select>
                       ) : (
                        getRouteDisplayText(code.route) // Use the mapping function here
                      )}
                    </td>
                    {/* Removed Created By cell */}
                    <td>{code.is_used ? 'Yes' : 'No'}</td> {/* is_used and used_at might require different edit logic or be non-editable */}
                    <td>{code.used_at ? new Date(code.used_at).toLocaleString() : 'N/A'}</td>
                    <td>
                      {editingId === code.id ? (
                        <>
                          <button onClick={() => handleSaveClick(code.id)}>Save</button>
                          <button onClick={handleCancelClick}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => handleEditClick(code)}>Edit</button>
                          <button onClick={() => handleEmailClick(code)}>Email</button> {/* Added Email button */}
                        </>
                      )}
                    </td>
                  </tr>
                )) 
                )}
              </tbody>
            </table>
            {/* Add invite code management components here */}

            {/* Render the email modal */}
            {showEmailModal && selectedInviteCode && (
              <SendInviteCodeModal
                inviteCode={selectedInviteCode}
                onClose={handleCloseEmailModal}
              />
            )}
          </div>
        );
      case 'other-settings':
        return (
          <div>
            <h2>Other Settings</h2>
            <p>Content for other admin settings will go here.</p>
            {/* Add other settings components here */}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-container">
      <h1>Admin Panel</h1>
      <div className="tabs">
        <button
          className={activeTab === 'invite-codes' ? 'active-tab' : ''}
          onClick={() => setActiveTab('invite-codes')}
        >
          Manage Invite-codes
        </button>
        <button
          className={activeTab === 'other-settings' ? 'active-tab' : ''}
          onClick={() => setActiveTab('other-settings')}
        >
          Other Settings
        </button>
      </div>
      <div className="tab-content">
        {renderContent()}
      </div>
    </div>
  );
}

export default Admin;
